import {
  ArrowRight,
  BookOpen,
  Play,
  Clock,
  Star,
  Users,
  Trophy,
  Plus,
  CircleDot,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { stripHtml } from "@/lib/sanitize";
import { Link, useLoaderData } from "react-router-dom";
import { HomepageLoaderData } from "@/routes/homePage/types/homePageDTO";
import { useTranslation } from "react-i18next";

export default function LearningDashboard() {
  const { t } = useTranslation();
  const loaderData = useLoaderData() as HomepageLoaderData | undefined;
  if (!loaderData) {
    throw Error; // Dashboard datası gelirken kullanıcıya bisi göstermek icin bisiler ayarlicaz buraya, burası düzeltilecek simdilik ufak bir yama
  }

  return (
    <>
      <main className="w-full px-4 sm:px-6 lg:px-8 xl:px-12 pb-4 sm:pb-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-white border border-gray-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    <span className="text-xl sm:text-2xl font-bold text-gray-900">
                      {loaderData.stats.enrolledCourses}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t("homepage.enrolledCourses")}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary-600 text-xs sm:text-sm"
                >
                  <Link to={"/my-courses"}>
                    <span className="hidden sm:inline">
                      {t("common.viewAll")}
                    </span>
                  </Link>
                  <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 sm:ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    <span className="text-xl sm:text-2xl font-bold text-gray-900">
                      {loaderData.stats.bookmarks}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t("homepage.bookmarks")}
                  </p>
                </div>
                <Link to={"/bookmarks"}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary-600 text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">
                      {t("common.viewAll")}
                    </span>
                    <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 sm:ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Star className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    <span className="text-xl sm:text-2xl font-bold text-gray-900">
                      {loaderData.stats.certificates}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t("homepage.myCertificates")}
                  </p>
                </div>
                <Link to={"/certificates"}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-primary hover:text-primary-600 text-xs sm:text-sm"
                  >
                    <span className="hidden sm:inline">
                      {t("common.viewAll")}
                    </span>
                    <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 sm:ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-gray-200">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center space-x-2 mb-2">
                    <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                    <span className="text-xl sm:text-2xl font-bold text-gray-900">
                      {loaderData.stats.completedCourses}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600">
                    {t("homepage.completedCourses")}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-primary hover:text-primary-600 text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">
                    {t("common.viewAll")}
                  </span>
                  <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 sm:ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="xl:col-span-3 space-y-6 sm:space-y-8">
            {/* Learning Progress */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg text-gray-900">
                    {t("homepage.letsStartLearning")}
                  </CardTitle>
                  <Link to={"/my-courses"}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-primary hover:text-primary-600 self-start sm:self-auto"
                    >
                      {t("common.seeAll")}
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                {/* When user has courses - replace the empty state with this */}
                {loaderData.inProgressCourses.length > 0 && (
                  <div className="space-y-4">
                    {loaderData.inProgressCourses.map(
                      (inProgressCourse, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <BookOpen className="h-6 w-6 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                              {inProgressCourse.name}
                            </h3>
                            {/*<p className="text-xs sm:text-sm text-gray-600 mt-1">Bölüm 4: Entity Framework Temelleri</p>*/}
                            <div className="flex items-center space-x-2 mt-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full"
                                  style={{
                                    width: `${inProgressCourse.progress}%`,
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-600">
                                %{inProgressCourse.progress}
                              </span>
                            </div>
                          </div>
                          <Link
                            to={"/course/" + inProgressCourse.id + "/learning"}
                          >
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary-600 text-white text-xs sm:text-sm"
                            >
                              {t("common.continue")}
                            </Button>
                          </Link>
                        </div>
                      ),
                    )}
                  </div>
                )}
                {loaderData.inProgressCourses.length === 0 && (
                  <div className="text-center py-8 sm:py-12">
                    <BookOpen className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                      {t("homepage.noCourseYet")}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-6">
                      {t("homepage.startLearningJourney")}
                    </p>
                    <Button className="bg-primary hover:bg-primary-600 text-white text-sm sm:text-base">
                      <Plus className="h-4 w-4 mr-2" />
                      {t("homepage.exploreCourses")}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recommended Courses */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="pb-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg text-gray-900">
                    {t("homepage.recommendedCourses")}
                  </CardTitle>
                  <Link to={"/courses"}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-primary hover:text-primary-600 self-start sm:self-auto"
                    >
                      Tümünü gör
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {loaderData.recommendedCourses.map((course, index) => (
                    <div
                      key={course.id || index}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow flex flex-col"
                    >
                      <div
                        className="aspect-video bg-gray-100 flex items-center justify-center bg-cover bg-center relative"
                        style={{
                          backgroundImage: course.imageUrl
                            ? `url(${course.imageUrl})`
                            : undefined,
                        }}
                      >
                        {/* Overlay for better text readability */}
                        <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                        <div className="text-center relative z-10">
                          <Play className="h-8 w-8 sm:h-12 sm:w-12 text-white mx-auto mb-2 drop-shadow-lg" />
                          <p className="text-xs sm:text-sm text-white drop-shadow-lg">
                            {t("homepage.coursePreview")}
                          </p>
                        </div>
                      </div>
                      <div className="p-3 sm:p-4 flex flex-col flex-1">
                        <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">
                          {course.name}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mb-3 flex-1">
                          {stripHtml(course.description ?? "")}
                        </p>
                        <div className="flex items-center justify-between mt-auto">
                          <span className="text-base sm:text-lg font-bold text-gray-900">
                            ₺{course.price}
                          </span>
                          <Link to={`/course/${course.id}`}>
                            <Button
                              size="sm"
                              className="bg-primary hover:bg-primary-600 text-white text-xs sm:text-sm"
                            >
                              {t("common.goToCourse")}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="xl:col-span-1 space-y-4 sm:space-y-6">
            {/* Recommendations */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-base sm:text-lg text-gray-900">
                  {t("homepage.selectedForYou")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {loaderData.selectedCourse ? (
                    <div className="border border-gray-200 rounded-lg p-3 sm:p-4">
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm">
                            {loaderData.selectedCourse.name}
                          </h4>
                          <p className="text-xs text-gray-600 mt-1">
                            {stripHtml(loaderData.selectedCourse.description ?? "")}
                          </p>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-xs sm:text-sm font-medium text-gray-900">
                              {loaderData.selectedCourse.priceText}
                            </span>
                            <Link
                              to={`/course/${loaderData.selectedCourse.id}`}
                            >
                              <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 text-gray-400" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 sm:py-8">
                      <Trophy className="h-12 w-12 sm:h-16 sm:w-16 text-primary mx-auto mb-4" />
                      <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                        {t("homepage.congratulations")}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600">
                        {t("homepage.allCoursesOwned")}
                      </p>
                    </div>
                  )}

                  <div className="text-center py-4 sm:py-6">
                    <Users className="h-8 w-8 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-xs sm:text-sm text-gray-600 mb-4">
                      {t("homepage.exploreMore")}
                    </p>
                    <Link to={"/courses"}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-primary text-primary hover:bg-primary-50 bg-transparent text-xs sm:text-sm"
                      >
                        {t("homepage.allCourses")}
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/*/!* Getting Started *!/*/}
            {/*<Card className="bg-white border border-gray-200">*/}
            {/*    <CardHeader className="pb-4">*/}
            {/*        <CardTitle className="text-base sm:text-lg text-gray-900">Başlangıç Rehberi</CardTitle>*/}
            {/*    </CardHeader>*/}
            {/*    <CardContent className="space-y-3">*/}
            {/*        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">*/}
            {/*            <div*/}
            {/*                className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-100 rounded-full flex items-center justify-center">*/}
            {/*                <span className="text-xs font-medium text-primary-600">1</span>*/}
            {/*            </div>*/}
            {/*            <span className="text-xs sm:text-sm text-gray-700">İlk kursunu seç</span>*/}
            {/*        </div>*/}
            {/*        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">*/}
            {/*            <div*/}
            {/*                className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-100 rounded-full flex items-center justify-center">*/}
            {/*                <span className="text-xs font-medium text-primary-600">2</span>*/}
            {/*            </div>*/}
            {/*            <span className="text-xs sm:text-sm text-gray-500">Öğrenmeye başla</span>*/}
            {/*        </div>*/}
            {/*        <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-50">*/}
            {/*            <div*/}
            {/*                className="w-5 h-5 sm:w-6 sm:h-6 bg-primary-100 rounded-full flex items-center justify-center">*/}
            {/*                <span className="text-xs font-medium text-primary-600">3</span>*/}
            {/*            </div>*/}
            {/*            <span className="text-xs sm:text-sm text-gray-500">İlk sertifikanı al</span>*/}
            {/*        </div>*/}
            {/*    </CardContent>*/}
            {/*</Card>*/}

            {/* Popular Categories */}
            <Card className="bg-white border border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-base sm:text-lg text-gray-900">
                  {t("homepage.popularCategories")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {loaderData.popularCategories.map((category, index) => (
                  <Button
                    key={category.id || index}
                    variant="ghost"
                    className="w-full justify-start text-gray-700 hover:text-primary hover:bg-primary-50 text-xs sm:text-sm"
                  >
                    <CircleDot className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-primary" />
                    {category.name}
                  </Button>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </>
  );
}
